import { TextFieldWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Keys } from '~commonInfra/utils/Keys';

import { Messages } from '../../../codegen/Localize';
import { HeaderMenuItem } from './HeaderMenuItem';
import { HeaderStyles } from './HeaderStyles';
import { HelpMenuViewComponentProps } from './HelpMenu';

export interface HelpMenuPanelProps extends HelpMenuViewComponentProps {
  itemSelected: () => void;
}

export class HelpMenuPanel extends React.Component<HelpMenuPanelProps> {

  static displayName = 'HelpMenuPanel';
  static width = 360;

  static element = React.createFactory(HelpMenuPanel);

  private static helpMenuContainerStyle = _.extend({}, HeaderStyles.MenuPanelStyle, {
    height: 'auto',
    width: HelpMenuPanel.width
  });

  private readonly boundItemSelected: () => void;
  private readonly boundShowAbout: () => void;
  private readonly boundContactSiteAdmin: () => void;
  public helpItems: any[];
  private searchBoxRef = React.createRef<HTMLInputElement>();

  constructor(props: HelpMenuPanelProps) {
    super(props);
    this.boundItemSelected = () => this.props.itemSelected();
    this.boundShowAbout = () => {
      // first call `itemSelected` to close the panel, and focus would return to the dropdown button
      this.props.itemSelected();

      // use setTimeout to ensure `showAbout`, which opens up a dialog, happens after focus went back to the dropdown button,
      // so focus could return to the dropdown button again when the dialog is closed
      setTimeout(() => this.props.helpActions.showAbout());
    };
  }

  public render(): React.DOMElement<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    this.helpItems = [this.renderSearchBox(), ...this.renderMenuItems()];
    return React.createElement('div', {
      id: 'help-menu-panel',
      style: HelpMenuPanel.helpMenuContainerStyle,
      'data-tb-test-id': 'help-menu-panel'
    },
      this.helpItems
    );
  }

  private handleSearchBoxKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === Keys.Enter && this.searchBoxRef.current) {
      const searchTerm = this.searchBoxRef.current.value;
      this.searchBoxRef.current.value = '';
      this.props.helpActions.searchHelp(searchTerm);
    }
  };

  private renderSearchBox() {
    return <TextFieldWidget
      aria-label={Messages.searchHelp()}
      key='search'
      kind='search'
      label=''
      onKeyDown={this.handleSearchBoxKeyDown}
      placeholder={Messages.searchHelp()}
      ref={this.searchBoxRef}
      style={{ width: HelpMenuPanel.width }}
      testId='help-menu-search-box'
    />;
  }

  private renderMenuItems() {
    const menuItems: React.ReactElement<any>[] = [
      HeaderMenuItem.element({
        key: 'help',
        label: this.props.serverHelpLabel,
        url: this.props.serverHelpUrl,
        target: '_blank',
        onClick: this.boundItemSelected,
        tbTestId: 'help-menu-server-help',
      })
    ];

    if (!this.props.isOfflineHelpEnabled) {
      menuItems.push(
        HeaderMenuItem.element({
          key: 'support',
          label: Messages.support(),
          url: this.props.supportUrl,
          target: '_blank',
          onClick: this.boundItemSelected,
          tbTestId: 'help-menu-support',
        }));
    }

    if(this.props.serverEnvironment === 'online') {
      menuItems.push(
        HeaderMenuItem.element({
          key: 'systemStatus',
          label: Messages.systemStatus(),
          url: this.props.systemStatusUrl,
          target: '_blank',
          onClick: this.boundItemSelected,
          tbTestId: 'system-status',
        })
      );
    }

    if (FeatureFlags.whatIsNewMenuOptionEnabled) {
        menuItems.push(
            HeaderMenuItem.element({
                key: 'whatIsNew',
                label: Messages.whatIsNew(),
                url: this.props.whatIsNewUrl,
                target: '_blank',
                onClick: this.boundItemSelected,
                tbTestId: 'what-is-new',
            }));
    }

    menuItems.push(
      HeaderMenuItem.element({
        key: 'aboutServer',
        label: Messages.aboutTableau(),
        onClick: this.boundShowAbout,
        tbTestId: 'help-menu-about-server',
      }));

    return menuItems;
  }

}
